/**
 * Social Login Confirm Page
 *
 * Displayed when a user clicks a social login button and needs to confirm
 * they want to continue with the social provider. This is an allauth-provided
 * intermediate confirmation step.
 */
import { Box, Button, Typography } from "@mui/material";
import { AuthLayout, BRAND_COLORS } from "../components/AuthLayout";
import { useAuthContext } from "../hooks/useAuthContext";
import { gettext, interpolate } from "@services/i18n";

// Translation constants
const CONNECT_PROVIDER = gettext("Connect %s");
const CONTINUE_WITH_PROVIDER = gettext("Continue with %s");
const CONNECT_DESCRIPTION = gettext(
  "You are about to connect a new third party account from %s."
);
const SIGN_IN_DESCRIPTION = gettext(
  "Sign in to Captured Knowledge using your %s account."
);
const CONTINUE_BUTTON = gettext("Continue");

// Microsoft logo SVG component
function MicrosoftLogo() {
  return (
    <svg
      width="48"
      height="48"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  );
}

export function SocialLoginConfirmPage() {
  const context = useAuthContext() as ReturnType<typeof useAuthContext> & {
    provider?: { id: string; name: string };
    process?: "login" | "connect";
    actionUrl?: string;
  };

  const provider = context.provider || { id: "microsoft", name: "Microsoft" };
  const process = context.process || "login";
  const actionUrl = context.actionUrl || "";
  const { csrfToken } = context;

  const isConnect = process === "connect";
  const isMicrosoft =
    provider.id === "microsoft" ||
    provider.id.toLowerCase().includes("microsoft");

  const title = isConnect
    ? interpolate(CONNECT_PROVIDER, [provider.name])
    : interpolate(CONTINUE_WITH_PROVIDER, [provider.name]);

  const description = isConnect
    ? interpolate(CONNECT_DESCRIPTION, [provider.name])
    : interpolate(SIGN_IN_DESCRIPTION, [provider.name]);

  return (
    <AuthLayout title={title}>
      <Box
        sx={{
          padding: "0 2.5rem 1.5rem",
          backgroundColor: "white",
          textAlign: "center",
        }}
      >
        {/* Provider Logo */}
        {isMicrosoft && (
          <Box sx={{ marginBottom: "1.5rem" }}>
            <MicrosoftLogo />
          </Box>
        )}

        <Typography
          sx={{
            fontSize: "0.95rem",
            color: BRAND_COLORS.text.secondary,
            lineHeight: 1.6,
            marginBottom: "2rem",
          }}
        >
          {description}
        </Typography>

        {/* Form submission */}
        <Box
          component="form"
          method="post"
          action={actionUrl}
          sx={{ width: "100%" }}
        >
          <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              backgroundColor: BRAND_COLORS.primary,
              color: "white",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "0.9375rem",
              fontWeight: 600,
              textTransform: "none",
              fontFamily: "'Montserrat', sans-serif",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: BRAND_COLORS.primaryHover,
                boxShadow: "none",
              },
            }}
            data-testid="social-login-continue-button"
          >
            {CONTINUE_BUTTON}
          </Button>
        </Box>
      </Box>
    </AuthLayout>
  );
}
